import { DisplayDateTime } from "src/base-components/Date";
import { ColumnsConfig } from "src/cases/caseView/blocks/cellTypes";
import { RecordDiffTable } from "src/cases/caseView/history/events/RecordDiffTable";
import { ReshapedRecordDiff } from "src/cases/caseView/history/events/ReshapedRecordDiff";
import { SingleRecordDiffTable } from "src/cases/caseView/history/events/SingleRecordDiffTable";
import {
  countTableRecordChanges,
  TableRecordDiff,
} from "src/cases/caseView/history/events/tableBlockDiff";
import { UserPill } from "src/cases/common/UserPill";
import { TableEditMode } from "src/clients/case-management";
import { Modal } from "src/design-system/Modal";
import { Pill } from "src/design-system/Pill";
import { pluralize } from "src/utils/stringUtils";

type TableBlockChangesModalProps = {
  open: boolean;
  onClose: () => void;
  afterLeave: () => void;
  blockTitle: string;
  editedByUserId?: string | null;
  editedAt: string;
  diff: TableRecordDiff;
  columnsConfig: ColumnsConfig;
  defaultColumnsEditMode?: TableEditMode;
};

export const TableBlockChangesModal: React.FC<TableBlockChangesModalProps> = ({
  open,
  onClose,
  afterLeave,
  blockTitle,
  editedByUserId,
  editedAt,
  diff,
  columnsConfig,
  defaultColumnsEditMode,
}) => {
  const changeCount = countTableRecordChanges(diff);

  return (
    <Modal afterLeave={afterLeave} open={open} size="xl" onClose={onClose}>
      <Modal.Header>
        <div className="flex items-center justify-between gap-x-2">
          <span>Inspect changes on “{blockTitle}”</span>
          <Pill dataLoc="table-changes-count" size="sm" variant="gray">
            <Pill.Text>{pluralize(changeCount, "change")}</Pill.Text>
          </Pill>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-y-4">
          <div className="flex items-center gap-x-1 font-inter-normal-12px text-gray-500">
            Edited by {editedByUserId && <UserPill userId={editedByUserId} />}{" "}
            on{" "}
            <span className="font-inter-medium-12px text-gray-800">
              <DisplayDateTime value={editedAt} />
            </span>
          </div>
          {diff.kind === "single" ? (
            <SingleRecordDiffTable
              afterRecord={diff.after}
              beforeRecord={diff.before}
              columnsConfig={columnsConfig}
            />
          ) : diff.kind === "reshaped" ? (
            <ReshapedRecordDiff
              after={diff.after}
              before={diff.before}
              columnsConfig={columnsConfig}
            />
          ) : (
            <RecordDiffTable
              afterRecord={diff.after}
              beforeRecord={diff.before}
              columnsConfig={columnsConfig}
              defaultColumnsEditMode={defaultColumnsEditMode}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Footer primaryButton={false} secondaryButton="Close" />
    </Modal>
  );
};
